<template>
    <Dialog v-model:open="open">
        <DialogContent class="sm:max-w-[425px]">
            <DialogHeader>
                <DialogTitle>Whitelist IP</DialogTitle>
            </DialogHeader>
            <form @submit="onSubmit">
                <div class="space-y-3">
                    <FormField name="ip" v-slot="{ componentField }">
                        <FormItem>
                            <FormControl>
                                <Input v-bind="componentField" placeholder="IP" focus />
                            </FormControl>
                            <FormDescription>
                                Enter the IP address you want to whitelist for
                                {{ server?.host }}.
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                </div>
                <DialogFooter>
                    <Button type="button" variant="secondary" :disabled="saving" @click="close"
                        >Cancel</Button
                    >
                    <Button
                        type="submit"
                        :loading="saving"
                        :disabled="meta.valid === false || meta.dirty === false"
                        >Whitelist</Button
                    >
                </DialogFooter>
            </form>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { cpanelGraphQLClient } from "@/composables/default-urql-client";
import { toTypedSchema } from "@vee-validate/zod";
import gql from "graphql-tag";
import { useForm } from "vee-validate";
import { ref, watch } from "vue";
import { toast } from "vue-sonner";
import * as z from "zod";

const props = defineProps<{
    server: {
        id: string;
    };
}>();

const open = defineModel();

const emit = defineEmits(["success"]);
const loading = ref(false);
const saving = ref(false);
const server = ref(null);

const schema = z.object({
    ip: z.string().min(1, {
        message: "IP is required.",
    }),
});

type Form = z.infer<typeof schema>;

const { handleSubmit, meta, resetForm, setFieldValue } = useForm({
    validationSchema: toTypedSchema(schema),
});

watch(open, (isOpen) => {
    if (isOpen) {
        init();
    }
});

const init = async () => {
    try {
        const { data, error } = await cpanelGraphQLClient.query(
            gql`
                query editCpanelServer($id: String!) {
                    server(id: $id) {
                        id
                        title
                        host
                    }
                }
            `,
            {
                id: props.server.id,
            }
        );

        if (data) {
            server.value = data.server;
        }
    } finally {
        loading.value = false;
    }
};

const onSubmit = handleSubmit((values) => {
    save(values);
});

const close = () => {
    open.value = false;
};

const save = async (values: Form) => {
    saving.value = true;

    try {
        const { error } = await cpanelGraphQLClient.mutation(
            gql`
                mutation cpanelServerBlockIp($id: String!, $ip: String!) {
                    whitelistIP(id: $id, ip: $ip)
                }
            `,
            {
                id: props.server.id,
                ip: values.ip,
            }
        );

        if (!error) {
            toast.success("Success", {
                description: `IP was successfully whitelisted.`,
            });

            emit("success");
            close();
        }
    } finally {
        saving.value = false;
    }
};
</script>
